import React, { useEffect } from 'react';
import { useStore } from '../../../../../stores/root';

// assets
import { faCheck, faClose } from '@fortawesome/pro-light-svg-icons';

// services
import ValidationService from '../../../../../services/validation';
import VendorsService from '../../../../../services/data/vendors';

// props
import { IRoleResourceShortProps } from '../../../../../props/users/role';

// components
import { DefaultButton, Panel, PanelType, Pivot, PivotItem, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import PermissionsService from '../../../../../services/permissions';
import ErrorService from '../../../../../services/general/error';
import NoAccess from '../../../../uiframeworks/noAccess';
import Text from '../../../../typography/text';
import { IVendorResourceProps } from '../../../../../props/data/vendors';
import Label from '../../../../typography/label';
import { IDocumentProps, IFileProps } from '../../../../../props/general';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faXmarkCircle } from '@fortawesome/pro-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import CompanyService from '../../../../../services/users/company';
import { ICompanyResourceShort } from '../../../../../props/users/company';
import CompanyApprovalService from '../../../../../services/users/companies/approval';
import Details from '../../../../typography/details';
import FilesList from '../../../../uiframeworks/files/list';
import CompanyDocumentsList from '../../documents/list';
import LoadingComponent from '../../../../feedbacks/loading';

type VerifyCompanyDocumentsFormProps = {
    companyId: string;
    onDismissed(refresh?: boolean): void;
}

const VerifyCompanyDocumentsForm: React.FC<VerifyCompanyDocumentsFormProps> = (props: VerifyCompanyDocumentsFormProps) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [justification, setJustification] = React.useState<string>("");
    const [error, setError] = React.useState<any>({});
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [company, setCompany] = React.useState<ICompanyResourceShort | undefined>();
    //const [documents, setDocuments] = React.useState<

    let hasPermission = PermissionsService.hasPermission(['companies.approvals.documents_verify.all', 'companies.approvals.documents_reject.all'], user.permissions);

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        const _company = await CompanyService.get(props.companyId);

        setCompany(_company);
        setLoaded(true);
    }

    const _onSubmit = async (type: string, typeText: string) => {
        try {
            if (window.confirm("Are you sure you want to set company documents as " + typeText + "?")) {
                setSubmitting(true);

                const fd = new FormData();
                fd.append('justification', justification || "");
                fd.append('type', type);
                await CompanyApprovalService.create(props.companyId, fd);

                banner.add({
                    key: 'verify_company_documents_success',
                    variant: 'success',
                    icon: faCheck,
                    text: `Company "${company?.name}" documents are now ${typeText}`
                });
                props.onDismissed(true);
            }
        } catch (e) {
            banner.add({
                key: 'verify_company_documents_error',
                variant: 'error',
                icon: faClose,
                text: ErrorService.getMessage(e)
            });
            setSubmitting(false);
        }
    }

    return <Panel headerText={"Verify Company's Documents"}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed(false)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack tokens={{ childrenGap: 10 }}>
                {
                    !submitting && hasPermission ? <Stack horizontal tokens={{childrenGap: 10}} horizontalAlign={'space-between'}>
                        <Stack horizontal tokens={{childrenGap: 10}}>
                            <PrimaryButton disabled={(justification || "").trim() === ""} text={"Approve"} onClick={() => _onSubmit('documents_verified', 'verified')} />
                            <DefaultButton disabled={(justification || "").trim() === ""} text={"Reject"} onClick={() => _onSubmit('documents_rejected', 'rejected')} />
                        </Stack>
                        <DefaultButton text={"Cancel"} onClick={() => props.onDismissed()} />
                    </Stack> : null
                }
                {submitting ? <LoadingComponent size={SpinnerSize.medium} labelPosition={"right"} label={"Submitting request ..."} spinnerPosition={'baseline'} /> : null}
            </Stack>;
        }}>
        <Stack>
            {!loaded ? <Stack horizontalAlign={"baseline"} styles={{ root: { marginTop: 20 } }}><Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Preparing form ..."} /></Stack> : null}
            {
                loaded && company && hasPermission ? <>
                    <Stack tokens={{ childrenGap: 20 }}>
                        <Text>Please verify company documents with details below</Text>
                        <Details title={'Name'} text={company.name} />
                        <Details title={'Address'} text={company.address} />
                        <Details title={'Phone number'} text={company.phoneNumber || "-"} />
                        <Details title={'Created by'} text={company.createdBy.name} />
                        <Stack className={'divider'} />
                        <Label size={'xsmall'} className={'color-darkgray'}>Documents</Label>
                        <CompanyDocumentsList hideCommandBar companyId={props.companyId} />
                        <Stack className={'divider'} />
                        <TextField label={"Justification"}
                            required={true}
                            value={justification}
                            multiline
                            autoAdjustHeight
                            resizable={false}
                            onChange={(evt, value) => {
                                const _justification = value || "";
                                
                                const validation = ValidationService.combination(value, ['required', 'limit'], {maxChars: 2000});
                                error.name = validation.message;

                                setJustification(_justification);
                                setError({...error});
                            }}
                            errorMessage={error.justification}
                            disabled={submitting} />
                    </Stack>
                </> : null
            }
            {!hasPermission ? <NoAccess /> : null}
        </Stack>
    </Panel>
};

export default VerifyCompanyDocumentsForm;
